import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Brain, AlertCircle, History, ArrowLeft, ArrowRight, LogOut } from 'lucide-react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { ResultsGraph } from '../components/ResultsGraph';
import { HarmonicGraph } from '../components/HarmonicGraph';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useResults } from '../contexts/ResultsContext';
import type { AssessmentResult } from '../types/results'; // @ts-ignore

export function RespondentDashboard() {
  const navigate = useNavigate();
  const { getAllResults, isLoading: isLoadingResults, error } = useResults();
  const [results, setResults] = useState<AssessmentResult[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [canRetake, setCanRetake] = useState(false);
  const [nextRetakeDate, setNextRetakeDate] = useState<Date | null>(null); // @ts-ignore
  const [hasAiAccess, setHasAiAccess] = useState(false);

  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  }, [navigate]);

  const loadResults = useCallback(async (userId: string) => {
    try {
      const allResults = await getAllResults(userId);
      
      if (allResults.length > 0) {
        setResults(allResults);

        // Get respondent's coach info to check AI access
        const respondentQuery = query(
          collection(db, 'respondents'),
          where('userId', '==', userId)
        );
        const respondentSnapshot = await getDocs(respondentQuery);
        
        if (!respondentSnapshot.empty) {
          const respondentData = respondentSnapshot.docs[0].data();
          const nextRetake = respondentData.nextRetakeDate?.toDate();
          const retakeEnabled = respondentData.retakeEnabled !== false;
          
          setCanRetake(retakeEnabled && (!nextRetake || nextRetake <= new Date()));
          setNextRetakeDate(nextRetake || null);

          // Check coach's tier for AI access
          if (respondentData.coachId) {
            const coachQuery = query(
              collection(db, 'coaches'),
              where('userId', '==', respondentData.coachId)
            );
            const coachSnapshot = await getDocs(coachQuery);
            
            if (!coachSnapshot.empty) {
              const coachData = coachSnapshot.docs[0].data();
              // Only advanced and partner tiers get AI access
              setHasAiAccess(coachData.tier === 'advanced' || coachData.tier === 'partner');
            }
          }
        }
      }
    } catch (err) {
      console.error('Error loading results:', err);
    }
  }, [getAllResults]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (!user) {
          navigate('/login');
          return;
        }

        await loadResults(user.uid);
      } finally {
        setIsAuthChecking(false);
      }
    });

    return () => unsubscribe();
  }, [navigate, loadResults]);

  const handlePreviousResult = () => {
    if (currentPage < results.length) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handleNextResult = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  if (isAuthChecking || isLoadingResults) {
    return <LoadingSpinner message="Loading your results..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200 mb-6">
            <div className="flex items-center gap-2 bg-red-500/10 text-red-600 p-4 rounded-xl mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
            </div>
            <Link
              to="/assessment"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium hover:scale-105 transition-all"
            >
              Take Assessment
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex justify-end mb-8">
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 
                hover:bg-gray-200 transition-all"
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">No Assessment Results Found</h2>
            <p className="text-gray-600 mb-6">You haven't completed an assessment yet. Take your first assessment to get personalized insights.</p>
            <Link
              to="/assessment"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium hover:scale-105 transition-all"
            >
              Take Assessment
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const selectedResult = results[currentPage - 1];
  const totalVersions = results.length;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Brain className="w-12 h-12 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Your Assessment Results</h1>
              <div className="flex items-center gap-2 text-gray-600">
                <span>Version {totalVersions - currentPage + 1}</span>
                {selectedResult.retakenAt && (
                  <span>• Retaken on {selectedResult.retakenAt.toLocaleDateString()}</span>
                )}
                <span>• Completed on {selectedResult.completedAt.toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {results.length > 1 && (
              <>
                <button
                  onClick={handleNextResult}
                  disabled={currentPage <= 1}
                  className="p-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all disabled:opacity-50"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="text-gray-600">
                  {currentPage} of {totalVersions}
                </div>
                <button
                  onClick={handlePreviousResult}
                  disabled={currentPage >= totalVersions}
                  className="p-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all disabled:opacity-50"
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              </>
            )}
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all"
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 shadow-sm border border-green-200"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-8">
              Characteristic Profile
            </h2>
            <ResultsGraph scores={selectedResult.scores} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-8 shadow-sm border border-purple-200"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-8">
              Harmonic Scale Profile
            </h2>
            <HarmonicGraph scores={selectedResult.harmonicScores} />
          </motion.div>
        </div>

        {selectedResult.aiAnalysis && hasAiAccess && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200 mb-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">AI Analysis</h2>
            <div className="prose max-w-none">
              <div className="text-gray-800 whitespace-pre-wrap">
                {selectedResult.aiAnalysis}
              </div>
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="flex flex-col items-center gap-4"
        >
          {canRetake && (
            <Link
              to="/assessment"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium hover:scale-105 transition-all"
            >
              Retake Assessment
            </Link>
          )}
          
          {!canRetake && nextRetakeDate && (
            <div className="text-gray-600 text-center">
              Next retake available on: {nextRetakeDate.toLocaleDateString()}
            </div>
          )}

          {hasAiAccess && (
            <Link
              to="/analysis"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium hover:scale-105 transition-all"
            >
              <Brain className="w-5 h-5" />
              Get New AI Analysis
            </Link>
          )}

          {!hasAiAccess && (
            <div className="text-gray-500 text-center">
              AI Analysis is not available with your current plan.
              Please contact your coach for more information.
            </div>
          )}

          {results.length > 1 && (
            <div className="flex items-center gap-2 text-gray-500 mt-4">
              <History className="w-4 h-4" />
              <span>Total Assessments: {results.length}</span>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}